import { httpsCallable } from 'firebase/functions';
import { auth, functions, getAppCheckHeader } from '../firebase.js';
import { USE_EMULATORS, FUNCTIONS_REGION, firebaseConfig } from '../firebaseConfig.js';

// The gateway is an onRequest (HTTP) function, not onCall, because we need
// a streamed response (SSE) for the studio's live "typing" preview —
// callable functions buffer the whole response. Auth is still enforced:
// we send the user's Firebase ID token as a Bearer header and the function
// verifies it server-side before calling OpenAI (see functions/index.js).

export function gatewayUrl(functionName) {
  if (USE_EMULATORS) {
    return `http://127.0.0.1:5001/${firebaseConfig.projectId}/${FUNCTIONS_REGION}/${functionName}`;
  }
  return `https://${FUNCTIONS_REGION}-${firebaseConfig.projectId}.cloudfunctions.net/${functionName}`;
}

// Mirrors functions/entitlement.js's MAX_INSTRUCTION_CHARS exactly — this
// isn't a security boundary (the server enforces its own copy regardless),
// it just means a too-long instruction (the learner's own long prompt, plus
// whatever the app itself invisibly folds in — comment-pin locators, the
// dynamic-topic technical recipe, an automated validation-retry message)
// fails instantly and with a clear message instead of after a full round
// trip to the gateway.
export const MAX_INSTRUCTION_CHARS = 200000;

// Rewrites a caught technical error into a short, friendly one for the UI —
// while preserving the ORIGINAL name/message/stack (plus `stage`, which
// specific step failed) as extra properties, so lib/errorLog.js can log the
// exact technical reason even though the user only ever sees `friendlyMessage`.
function rethrowFriendly(originalErr, friendlyMessage, stage) {
  const e = new Error(friendlyMessage);
  e.technicalName = originalErr?.name;
  e.technicalMessage = originalErr?.message;
  e.stack = originalErr?.stack || e.stack;
  e.stage = stage;
  throw e;
}

// streamHtmlAgentGeneration (formerly here) called functions/generateHtmlAgent,
// which had no remaining UI caller — src/views/studio.js has called
// streamPluginAgentGeneration below exclusively since that pipeline reached
// feature parity. Removed 2026-08-16 alongside the server-side endpoint (see
// functions/.archive-legacy-pipeline-removal-2026-08-16/).

/**
 * Streams a generation request to the plugin-agent gateway (functions/
 * pluginAgentHandler.js) — this pipeline's own loop mechanics (token-budget
 * auto-continue, todo-gate nudges, self-healing retries, repeated-tool-call
 * detection) faithfully mimic the html-coding-agent VS Code extension, with
 * an HTML+JS-only tool set. This is now the only generation pipeline end
 * users go through (see src/views/studio.js's handleSend).
 *
 * Image attachment, reference-page folding, and bgColorId/templateId ARE
 * forwarded (pluginAgentHandler.js reuses htmlAgentHandler.js's own
 * describeImageForTextOnlyModel/buildReferenceInstruction for the first two;
 * pins and Setup's template/look-and-feel briefs need no wire support at
 * all — studio.js folds them into `instruction`'s text before this is ever
 * called, same as streamHtmlAgentGeneration). Same single-request run model
 * (no resume, no background continuation) and mostly the same SSE event
 * vocabulary as the other pipeline — still no `plan` events, since nothing
 * here ever sends those.
 *
 * @param {object} opts
 * @param {string} opts.instruction
 * @param {string|null} opts.currentHtml
 * @param {string} opts.courseId
 * @param {string} opts.topicId
 * @param {string|null} [opts.generationSessionId]
 * @param {string|null} [opts.imageDataUrl]
 * @param {string|null} [opts.bgColorId]
 * @param {string|null} [opts.templateId]
 * @param {string|null} [opts.referenceHtml]
 * @param {string|null} [opts.referenceMode]
 * @param {(message:string, event:object)=>void} [opts.onStatus]
 * @param {(event:object)=>void} [opts.onToolCall]
 * @param {(event:object)=>void} [opts.onToolResult]
 * @param {(event:object)=>void} [opts.onToolCallDelta]
 * @param {(version:number)=>void} [opts.onVersion] - fires for a real build (the 'final' event) AND for a chat-driven edit that saved a new version (the 'answer' event) — check `onAnswer`/the resolved `editedVersion` to tell which happened.
 * @param {()=>void} [opts.onStoppedEarly] - called if a provider error cut a turn short after real content already existed — either a build (the 'final' event) or a chat-only reply (the 'answer' event, functions/chatAnswer.js's stoppedEarly field). The returned/rendered content is still real, just possibly incomplete.
 * @param {(info:{durationMs:number, isRefinement:boolean, summary?:string})=>void} [opts.onFinished]
 * @param {(included:boolean)=>void} [opts.onImageStatus]
 * @returns {Promise<string>} the final HTML document
 */
export async function streamPluginAgentGeneration({
  instruction, currentHtml, courseId, topicId, generationSessionId = null,
  imageDataUrl = null, bgColorId = null, templateId = null, referenceHtml = null, referenceMode = null,
  onStatus, onToolCall, onToolResult, onToolCallDelta, onSessionStarted, onVersion, onStoppedEarly, onFinished, onAnswer, onImageStatus,
}) {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');

  if (instruction && instruction.length > MAX_INSTRUCTION_CHARS) {
    throw new Error(`Your message is too long (${instruction.length.toLocaleString()} of ${MAX_INSTRUCTION_CHARS.toLocaleString()} characters) — shorten it and try again.`);
  }

  let finalHtml = '';

  let idToken;
  try {
    idToken = await user.getIdToken();
  } catch (err) {
    rethrowFriendly(err, 'Could not verify your session — refresh the page and try again.', 'getIdToken');
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 3660000);
  let res;
  try {
    res = await fetch(gatewayUrl('generatePluginAgent'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Firebase-Auth': idToken,
        ...(await getAppCheckHeader()),
      },
      body: JSON.stringify({
        instruction, currentHtml, courseId, topicId, generationSessionId,
        imageDataUrl, bgColorId, templateId, referenceHtml, referenceMode,
      }),
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeout);
    if (err.name === 'AbortError') {
      rethrowFriendly(err, 'Generation timed out. Ask for a smaller page or try again.', 'fetch');
    }
    rethrowFriendly(err, 'Could not reach the generation server. Check your connection and try again.', 'fetch');
  }

  if (!res.ok) {
    clearTimeout(timeout);
    const errText = await res.text().catch(() => '');
    let message = `Generation failed (${res.status})`;
    try { message = JSON.parse(errText).error || message; } catch (_) {}
    const httpErr = new Error(message);
    httpErr.stage = 'http';
    httpErr.httpStatus = res.status;
    httpErr.technicalMessage = errText;
    throw httpErr;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  // Set when the chat agent (functions/chatAnswer.js's answerOrEditChatTurn,
  // the chat-first FIRST hop every message goes through — no classifier,
  // see functions/pluginAgentHandler.js's header) answered or made a small
  // edit itself instead of escalating to a real build — there is no
  // page/version here, just a chat reply. ALSO set (same 'answer' event)
  // when the turn ended without writing anything — see
  // pluginAgentRunner.js's finalizePluginRun.
  let answered = null;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const frames = buffer.split('\n\n');
      buffer = frames.pop();

      for (const frame of frames) {
        const line = frame.split('\n').find((l) => l.startsWith('data:'));
        if (!line) continue;
        const payload = line.slice(5).trim();
        if (payload === '[DONE]') continue;
        try {
          const evt = JSON.parse(payload);
          if (evt.type === 'status') {
            onStatus?.(evt.message || 'Working...', evt);
          } else if (evt.type === 'assistantDelta') {
            onStatus?.('Thinking...', evt);
          } else if (evt.type === 'sessionStarted') {
            if (evt.sessionId) onSessionStarted?.({ sessionId: evt.sessionId });
          } else if (evt.type === 'toolCall') {
            onToolCall?.(evt);
          } else if (evt.type === 'toolResult') {
            onToolResult?.(evt);
          } else if (evt.type === 'toolCallDelta') {
            onToolCallDelta?.(evt);
          } else if (evt.type === 'model') {
            onStatus?.('Generating...', evt);
          } else if (evt.type === 'imageStatus') {
            onImageStatus?.(!!evt.included);
          } else if (evt.type === 'answer') {
            // The server answered a QUESTION directly, or the turn ended
            // without writing anything — either way the response ends right
            // after this frame, no 'final' frame is coming.
            answered = { text: evt.message || '' };
            // A provider error cut the chat agent's turn short after real
            // text already existed (functions/chatAnswer.js's stoppedEarly
            // field) — reuse the same onStoppedEarly signal the 'final'
            // event below fires, so studio.js's existing stopped-early
            // handling (previously build-turns-only) also covers a
            // truncated CHAT reply, instead of silently rendering a
            // mid-sentence answer with no indication anything was cut off.
            if (evt.stoppedEarly) onStoppedEarly?.();
            // A chat-driven EDIT (not just a question) that actually saved a
            // new version — functions/pluginAgentHandler.js only sets these
            // two fields when saveGeneratedVersion succeeded. Previously
            // dropped entirely: the server saved a real new version but the
            // client had nothing to update its preview/version selector
            // with, so the browser kept showing the pre-edit page for the
            // rest of the session even though the edit had genuinely
            // happened (the exact "it said it removed them but nothing
            // changed" report this fixes). Reuses onVersion — the same
            // callback the 'final' event below fires — so callers don't
            // need a new wire-up for this case.
            if (Number.isFinite(evt.version) && evt.version > 0) {
              answered.version = evt.version;
              answered.html = evt.html || '';
              onVersion?.(evt.version);
            }
          } else if (evt.type === 'error') {
            const genError = new Error(evt.message);
            genError.stage = 'server';
            if (evt.rawHtml) genError.rawHtml = evt.rawHtml;
            throw genError;
          } else if (evt.type === 'final') {
            finalHtml = evt.html;
            if (Number.isFinite(evt.version) && evt.version > 0) onVersion?.(evt.version);
            if (evt.stoppedEarly) onStoppedEarly?.();
            if (Number.isFinite(evt.durationMs)) {
              onFinished?.({
                durationMs: evt.durationMs,
                isRefinement: !!evt.isRefinement,
                summary: evt.summary || undefined,
                renderIssues: Array.isArray(evt.renderIssues) ? evt.renderIssues : undefined,
                autoFixAttempted: !!evt.autoFixAttempted,
                autoFixSucceeded: !!evt.autoFixSucceeded,
                stopReason: evt.stopReason || undefined,
              });
            }
          }
        } catch (e) {
          if (e instanceof SyntaxError) continue; // ignore partial JSON
          throw e;
        }
      }
    }
  } catch (err) {
    if (err.name === 'AbortError') {
      rethrowFriendly(err, 'Generation timed out. Ask for a smaller page or try again.', 'stream-read');
    }
    if (!err.stage) {
      rethrowFriendly(err, 'Lost connection to the generation server partway through — this is usually a network hiccup, not a problem with your request. Try again.', 'stream-read');
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }

  if (answered) {
    onAnswer?.(answered.text);
    return {
      html: answered.html || '', answered: true, answerText: answered.text, editedVersion: answered.version,
    };
  }

  if (!finalHtml.trim()) {
    const emptyErr = new Error('The coding agent finished without returning any HTML.');
    emptyErr.stage = 'empty-response';
    throw emptyErr;
  }
  return finalHtml;
}

/**
 * checkPageCompleteness — the on-demand "Check completeness" button (see
 * functions/completenessOnDemand.js). Re-checks an already-saved version
 * against the instruction that produced it, on a deliberately stronger
 * model than ordinary generation uses. This is a user-initiated action the
 * caller is actively waiting on, so errors are thrown rather than
 * swallowed; the button's own click handler is responsible for showing
 * them.
 */
export async function checkPageCompleteness({
  courseId, topicId, version, instruction,
}) {
  const fn = httpsCallable(functions, 'checkPageCompleteness');
  const { data } = await fn({
    courseId, topicId, version, instruction,
  });
  return data;
}

export async function stopAgentSession(sessionId) {
  const fn = httpsCallable(functions, 'stopAgentSession');
  const { data } = await fn({ sessionId });
  return data;
}
